import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Title,
  Text,
  Button,
  Paper,
  Group,
  Stack,
  TextInput,
  Select,
  NumberInput,
  Checkbox,
  Badge,
  SimpleGrid,
  Card,
  Image,
  Loader,
  Center,
  Box,
  ActionIcon,
  Pill,
  Modal,
  Avatar,
  ScrollArea,
  Switch
} from '@mantine/core';
import { productsAPI, personasAPI, recommendationsAPI } from '../services/api';

const ProductsPage = () => {
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    brand: '',
    minPrice: '',
    maxPrice: ''
  });
  const [metadata, setMetadata] = useState({
    categories: [],
    brands: [],
    priceRange: null
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [useAI, setUseAI] = useState(true);
  const [searchEbay, setSearchEbay] = useState(false);
  const [searchMock, setSearchMock] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [showPersonaSelector, setShowPersonaSelector] = useState(false);
  const [aiSearchQueries, setAiSearchQueries] = useState([]);
  const [friendPersonaData, setFriendPersonaData] = useState(null); // For shopping with friend's persona

  useEffect(() => {
    loadMetadata();
    loadAvailablePersonas();
  }, []);

  // Separate effect for handling URL parameters and initial loading
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const personaId = urlParams.get('persona');
    const initialSearch = urlParams.get('search');

    if (personaId) {
      // Check if it's a numeric persona ID (user's own persona)
      const numericId = parseInt(personaId);
      if (!isNaN(numericId) && personas.some(p => p.id === numericId)) {
        // Valid numeric persona ID that exists in user's personas
        setSelectedPersona(numericId);
      } else {
        // Non-numeric ID (like profile personas from friends) or invalid ID
        // Don't pre-select any persona, let user choose from their own
        console.log('Non-numeric or invalid persona ID, letting user choose from their own personas');
      }
    }

    if (initialSearch) {
      setSearchQuery(initialSearch);
    }

    // Load products based on URL params or show initial catalog
    // Only use persona_id if we have a valid numeric ID
    const validPersonaId = personaId && !isNaN(parseInt(personaId)) && personas.some(p => p.id === parseInt(personaId))
      ? parseInt(personaId)
      : null;

    loadProducts({
      persona_id: validPersonaId,
      search: initialSearch
    });
  }, [personas]); // Add personas dependency to re-check when loaded

  const loadAvailablePersonas = async () => {
    try {
      const response = await personasAPI.getAll();
      setPersonas(response.data || []);
    } catch (error) {
      console.error('Failed to load personas:', error);
      setPersonas([]);
    }
  };

  const loadMetadata = async () => {
    try {
      const response = await productsAPI.getMetadata();
      setMetadata(response.data);
    } catch (error) {
      console.error('Failed to load metadata:', error);
    }
  };

  const loadProducts = async (params = {}) => {
    try {
      setLoading(true);

      // If persona is selected and no search query, get AI recommendations
      if (selectedPersona && !searchQuery.trim()) {
        console.log(`🤖 Loading AI recommendations for persona ${selectedPersona}`);
        // Build marketplaces array from user toggles (empty = let backend decide)
        const marketplaces = [];
        if (searchEbay) marketplaces.push('ebay');
        if (searchMock) marketplaces.push('mock');

        const response = await recommendationsAPI.getRecommendations({
          subject_type: 'persona',
          subject_id: selectedPersona,
          query: null, // Explicit null for no query
          filters: {
            ...filters,
            ...params
          },
          marketplaces // pass marketplaces (may be empty array to indicate all)
        });

        setProducts(response.data.products || []);
        setAiSearchQueries(response.data.search_queries_used || []);
        console.log('🔍 Debug: AI search queries set to:', response.data.search_queries_used || []);
      } else {
        // Regular product search
        const response = await productsAPI.getAll({
          search: searchQuery || undefined,
          ...filters,
          ...params,
          limit: 50
        });

        setProducts(response.data.products || []);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    // Priority 1: Mock search (highest priority)
    if (searchMock) {
      try {
        setLoading(true);
        // Directly call mock search endpoint, bypassing global USE_MOCK_PRODUCTS flag
        const response = await axios.post('/products/mock/search', {
          query: searchQuery || undefined,
          category: filters.category || undefined,
          brand: filters.brand || undefined,
          minPrice: filters.minPrice ? parseFloat(filters.minPrice) : undefined,
          maxPrice: filters.maxPrice ? parseFloat(filters.maxPrice) : undefined,
          limit: 50
        });
        setProducts(response.data.products || []);
        setLoading(false);
        return;
      } catch (error) {
        console.error('Mock search failed:', error);
        setLoading(false);
        return;
      }
    }

    // Priority 2: eBay search
    if (searchEbay) {
      try {
        setLoading(true);
        const response = await productsAPI.searchEbay({
          query: searchQuery || undefined,
          category: filters.category || undefined,
          brand: filters.brand || undefined,
          minPrice: filters.minPrice ? parseFloat(filters.minPrice) : undefined,
          maxPrice: filters.maxPrice ? parseFloat(filters.maxPrice) : undefined,
          limit: 50
        });
        setProducts(response.data.products || []);
        setLoading(false);
        return;
      } catch (error) {
        console.error('eBay search failed:', error);
        setLoading(false);
        return;
      }
    }

    // Priority 3: AI search
    if (useAI && searchQuery.trim()) {
      try {
        setLoading(true);

        // If persona is selected, use persona-aware AI recommendations instead of generic AI search
        if (selectedPersona) {
          console.log(`🤖 Using persona-aware AI search for persona ${selectedPersona}`);
          // Build marketplaces array from user toggles (empty = let backend decide)
          const marketplaces = [];
          if (searchEbay) marketplaces.push('ebay');
          if (searchMock) marketplaces.push('mock');

          const response = await recommendationsAPI.getRecommendations({
            subject_type: 'persona',
            subject_id: selectedPersona,
            query: searchQuery, // Include the user's search query as context
            filters: {
              category: filters.category || undefined,
              brand: filters.brand || undefined,
              minPrice: filters.minPrice ? parseFloat(filters.minPrice) : undefined,
              maxPrice: filters.maxPrice ? parseFloat(filters.maxPrice) : undefined
            },
            marketplaces
          });
          setProducts(response.data.products || []);
          setAiSearchQueries(response.data.search_queries_used || []);
        } else {
          // Regular AI search for users without selected persona
          const response = await productsAPI.search({
            query: searchQuery,
            category: filters.category || undefined,
            brand: filters.brand || undefined,
            minPrice: filters.minPrice || undefined,
            maxPrice: filters.maxPrice || undefined,
            useAI: true
          });
          setProducts(response.data.products || []);
        }

        setLoading(false);
        return;
      } catch (error) {
        console.log('AI search failed, falling back to text search');
      }
    }

    // Fallback: Regular search
    await loadProducts();
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      brand: '',
      minPrice: '',
      maxPrice: ''
    });
    setSearchQuery('');
    loadProducts();
  };

  const handlePersonaSelect = (persona) => {
    setSelectedPersona(persona.id);
    setShowPersonaSelector(false);

    // Auto-fill search with persona name for better AI suggestions
    if (!searchQuery.trim()) {
      setSearchQuery(`gifts for ${persona.name}`);
    }

    loadProducts({ persona_id: persona.id });
  };

  const clearPersonaFilter = () => {
    setSelectedPersona(null);
    setAiSearchQueries([]);
    loadProducts();
  };

  const getSelectedPersonaDetails = () => {
    return personas.find(p => p.id === selectedPersona);
  };

  const formatPrice = (price) => {
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const popularSearches = [
    { label: '🎮 Gaming', query: 'gaming' },
    { label: '☕ Coffee', query: 'coffee machine' },
    { label: '🎧 Headphones', query: 'wireless headphones' },
    { label: '📚 Books', query: 'fantasy books' },
    { label: '🎨 Art Supplies', query: 'art supplies' },
    { label: '🏋️ Fitness', query: 'fitness equipment' }
  ];

  const selectedPersonaDetails = getSelectedPersonaDetails();

  return (
    <Box style={{ background: 'linear-gradient(180deg, rgba(59,130,246,0.03) 0%, rgba(0,0,0,0) 100%)' }}>
      <Container size="xl" py="xl" px="md">
        {/* Header */}
        <Paper p="xl" radius="lg" withBorder mb="xl" style={{
          background: 'linear-gradient(135deg, rgba(59,130,246,0.05) 0%, rgba(0,0,0,0) 100%)',
          borderColor: 'rgba(59,130,246,0.2)'
        }}>
          <Group justify="space-between" align="flex-start">
            <Box>
              <Group gap="md" align="center">
                <Box>
                  <Title
                    order={1}
                    size="h1"
                    fw={700}
                    style={{
                      background: 'linear-gradient(45deg, #3b82f6 0%, #8b5cf6 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      marginBottom: '8px'
                    }}
                  >
                    🎁 Browse Products
                  </Title>
                  <Text size="lg" c="dimmed">
                    Search for the perfect gift or browse our catalog
                  </Text>
                </Box>

                {/* Mock Data Indicator */}
                {productsAPI.isUsingMock() && (
                  <Badge
                    size="lg"
                    variant="filled"
                    color="orange"
                    leftSection="🎭"
                    style={{
                      animation: 'pulse 2s infinite',
                      fontSize: '14px'
                    }}
                  >
                    USING MOCK DATA
                  </Badge>
                )}
              </Group>
            </Box>

            {/* Shopping For Selector */}
            <Box>
              {selectedPersonaDetails ? (
                <Paper p="md" withBorder radius="md" style={{ background: 'rgba(139,92,246,0.05)' }}>
                  <Group gap="sm">
                    <Avatar radius="xl" color="grape" size="sm">
                      {selectedPersonaDetails.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Text size="xs" c="dimmed">Shopping for</Text>
                      <Text size="sm" fw={600}>{selectedPersonaDetails.name}</Text>
                    </Box>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="red"
                      onClick={clearPersonaFilter}
                    >
                      ✕
                    </ActionIcon>
                  </Group>
                </Paper>
              ) : (
                <Button
                  variant="light"
                  color="grape"
                  leftSection="👤"
                  onClick={() => setShowPersonaSelector(true)}
                >
                  Select Who You're Shopping For
                </Button>
              )}
            </Box>
          </Group>
        </Paper>

        {/* AI Search Queries Display - only show when AI recommendations are active */}
        {aiSearchQueries.length > 0 && selectedPersona && !searchQuery.trim() && (
          <Paper p="lg" radius="lg" withBorder mb="xl" style={{ background: 'rgba(139,92,246,0.03)' }}>
            <Stack gap="md">
              <Group>
                <Text size="sm" c="dimmed">🤖 AI-Generated Search Terms:</Text>
                <Badge size="sm" variant="light" color="grape">
                  {aiSearchQueries.length} queries used
                </Badge>
              </Group>
              <Group gap="xs">
                {aiSearchQueries.map((item, index) => (
                  <Pill
                    key={index}
                    size="md"
                    style={{
                      cursor: 'pointer',
                      background: 'rgba(139,92,246,0.1)',
                      border: '1px solid rgba(139,92,246,0.3)'
                    }}
                    onClick={() => setSearchQuery(item.query)}
                  >
                    "{item.query}"
                  </Pill>
                ))}
              </Group>
              <Text size="xs" c="dimmed">
                💡 Click any search term to use it for manual AI search, or adjust the persona's preferences to get better matches
              </Text>
            </Stack>
          </Paper>
        )}

        {/* Search Section */}
        <Paper p="xl" radius="lg" withBorder mb="xl">
          <form onSubmit={handleSearch}>
            <Stack gap="md">
              <Group gap="xs" grow>
                <TextInput
                  placeholder="Search for products... (e.g., 'gaming laptop', 'coffee maker')"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="lg"
                  leftSection="🔍"
                />
                <Button
                  type="submit"
                  size="lg"
                  variant="gradient"
                  gradient={{ from: 'blue', to: 'violet' }}
                  style={{ minWidth: '200px' }}
                >
                  {useAI ? '🤖 AI Search' : '🔍 Search'}
                </Button>
              </Group>

              <Group gap="md">
                <Checkbox
                  checked={useAI}
                  onChange={(e) => setUseAI(e.currentTarget.checked)}
                  label="Use AI-powered search (searches all marketplaces)"
                />
                <Checkbox
                  checked={searchEbay}
                  onChange={(e) => setSearchEbay(e.currentTarget.checked)}
                  label="🔍 Search eBay only"
                />
                <Checkbox
                  checked={searchMock}
                  onChange={(e) => setSearchMock(e.currentTarget.checked)}
                  label="🎭 Search Mock only"
                />
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  {showAdvanced ? 'Hide' : 'Show'} Filters
                </Button>
              </Group>

              {useAI && !searchEbay && !searchMock && (
                <Paper p="md" radius="md" style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)' }}>
                  <Group gap="xs">
                    <Text size="sm" c="dimmed">ℹ️ AI search will query <strong>all available marketplaces</strong> for the best results.</Text>
                  </Group>
                </Paper>
              )}

              {showAdvanced && (
                <Paper p="md" withBorder radius="md" style={{ background: 'rgba(59,130,246,0.02)' }}>
                  <Stack gap="md">
                    {/* Development Tools */}
                    <Group justify="space-between" align="center">
                      <Text size="sm" fw={600} c="dimmed">🛠️ Development Tools:</Text>
                      <Group gap="md">
                        <Group gap="xs" align="center">
                          <Text size="xs" c="dimmed">Mock Data:</Text>
                          <Switch
                            size="sm"
                            color="orange"
                            checked={productsAPI.isUsingMock()}
                            onChange={(e) => {
                              const useMock = e.currentTarget.checked;
                              // Reload the page with mock parameter to switch data source
                              const url = new URL(window.location);
                              if (useMock) {
                                url.searchParams.set('mock', 'true');
                              } else {
                                url.searchParams.delete('mock');
                              }
                              window.location.href = url.toString();
                            }}
                          />
                        </Group>
                        {productsAPI.isUsingMock() && (
                          <Button
                            size="xs"
                            variant="light"
                            color="orange"
                            onClick={() => {
                              // Regenerate mock products
                              productsAPI.generateMockProducts({ count: 50 })
                                .then(() => {
                                  // Reload metadata and products
                                  loadMetadata();
                                  loadProducts();
                                })
                                .catch(console.error);
                            }}
                          >
                            🔄 Regenerate
                          </Button>
                        )}
                      </Group>
                    </Group>

                    <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
                      <Select
                        label="Category"
                        placeholder="All Categories"
                        data={metadata.categories.map(cat => ({ value: cat, label: cat }))}
                        value={filters.category}
                        onChange={(value) => setFilters({ ...filters, category: value })}
                        clearable
                      />

                      <Select
                        label="Brand"
                        placeholder="All Brands"
                        data={metadata.brands.map(brand => ({ value: brand, label: brand }))}
                        value={filters.brand}
                        onChange={(value) => setFilters({ ...filters, brand: value })}
                        clearable
                      />

                      <NumberInput
                        label="Min Price"
                        placeholder="$0"
                        value={filters.minPrice}
                        onChange={(value) => setFilters({ ...filters, minPrice: value })}
                        prefix="$"
                        min={0}
                      />

                      <NumberInput
                        label="Max Price"
                        placeholder="$1000"
                        value={filters.maxPrice}
                        onChange={(value) => setFilters({ ...filters, maxPrice: value })}
                        prefix="$"
                        min={0}
                      />
                    </SimpleGrid>

                    <Button variant="subtle" size="sm" onClick={clearFilters}>
                      Clear All Filters
                    </Button>
                  </Stack>
                </Paper>
              )}

              {/* Popular Searches */}
              <Box>
                <Text size="sm" c="dimmed" mb="xs">Popular:</Text>
                <Group gap="xs">
                  {popularSearches.map((item, index) => (
                    <Pill
                      key={index}
                      size="md"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSearchQuery(item.query)}
                    >
                      {item.label}
                    </Pill>
                  ))}
                </Group>
              </Box>
            </Stack>
          </form>
        </Paper>

        {/* Products Grid */}
        {loading ? (
          <Center h={400}>
            <Stack align="center" gap="md">
              <Loader size="xl" type="dots" />
              <Text size="lg" c="dimmed">
                {selectedPersona && !searchQuery.trim()
                  ? '🤖 Getting AI recommendations...'
                  : searchEbay
                  ? '🔍 Searching eBay for products...'
                  : searchMock
                  ? '🎭 Searching mock products...'
                  : 'Searching products...'
                }
              </Text>
            </Stack>
          </Center>
        ) : products.length === 0 ? (
          <Center h={400}>
            <Stack align="center" gap="md">
              <Text size="60px">📦</Text>
              <Title order={3}>No products found</Title>
              <Text c="dimmed" ta="center" maw={500}>
                Try adjusting your search terms or filters
              </Text>
            </Stack>
          </Center>
        ) : (
          <>
            <Badge size="lg" variant="light" color="blue" mb="md">
              Showing {products.length} products
            </Badge>

            <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="lg">
              {products.map(product => (
                <Card
                  key={product.id || product.ebay_item_id}
                  shadow="sm"
                  padding="lg"
                  radius="md"
                  withBorder
                  style={{
                    transition: 'all 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <Card.Section>
                    <Center h={200} style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
                      {product.image_url && product.image_url !== '' &&
                       !product.image_url.includes('sandbox.ebay.com') ? (
                        <Image
                          src={product.image_url}
                          alt={product.name}
                          height={180}
                          fit="contain"
                          fallbackSrc="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE4MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmN2ZhIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0PiA8L3N2Zz4="
                          onError={(e) => {
                            e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE4MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIzMCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPoKgPC90ZXh0Pjwvc3ZnPg==";
                          }}
                        />
                      ) : (
                        <Text size="60px">📦</Text>
                      )}
                    </Center>
                  </Card.Section>

                  <Stack gap="xs" mt="md">
                    <Group gap="xs">
                      {product.brand && (
                        <Badge size="xs" variant="light">{product.brand}</Badge>
                      )}

                      {/* Source Tag */}
                      <Badge
                        size="xs"
                        variant="filled"
                        color={
                          product.source === 'mock' ? 'orange' :
                          product.source === 'ebay' ? 'blue' :
                          product.source === 'vector_search' ? 'green' :
                          'gray'
                        }
                        leftSection={
                          product.source === 'mock' ? '🎭' :
                          product.source === 'ebay' ? '🛒' :
                          product.source === 'vector_search' ? '🔍' :
                          '🏪'
                        }
                      >
                        {product.source === 'mock' ? 'Mock' :
                         product.source === 'ebay' ? 'eBay' :
                         product.source === 'vector_search' ? 'AI Search' :
                         'Internal'}
                      </Badge>
                    </Group>

                    <Text fw={600} size="md" lineClamp={2}>
                      {product.name}
                    </Text>

                    {product.category && (
                      <Text size="xs" c="dimmed">{product.category}</Text>
                    )}

                    <Text size="xl" fw={700} c="blue">
                      {formatPrice(product.price)}
                    </Text>

                    <Group gap="xs" grow>
                      <Button
                        variant="light"
                        size="sm"
                        component={product.url ? 'a' : 'button'}
                        href={product.url || undefined}
                        target={product.url ? '_blank' : undefined}
                        rel={product.url ? 'noopener noreferrer' : undefined}
                        disabled={!product.url}
                      >
                        View Details
                      </Button>
                      <Button variant="filled" size="sm" color="violet">
                        + Wishlist
                      </Button>
                    </Group>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
          </>
        )}
      </Container>

      {/* Persona Selector Modal */}
      <Modal
        opened={showPersonaSelector}
        onClose={() => setShowPersonaSelector(false)}
        title={<Text size="xl" fw={700}>👤 Who Are You Shopping For?</Text>}
        size="lg"
        centered
      >
        <Stack gap="md">
          <Text c="dimmed">
            Select a persona to get personalized product recommendations based on their preferences
          </Text>

          <ScrollArea h={400}>
            <Stack gap="xs">
              {personas.length === 0 ? (
                <Center py="xl">
                  <Stack align="center" gap="md">
                    <Text size="40px">👤</Text>
                    <Text c="dimmed">No personas found</Text>
                    <Button component="a" href="/personas/new" variant="light">
                      Create Your First Persona
                    </Button>
                  </Stack>
                </Center>
              ) : (
                personas.map(persona => (
                  <Paper
                    key={persona.id}
                    p="md"
                    withBorder
                    radius="md"
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateX(4px)';
                      e.currentTarget.style.borderColor = 'var(--mantine-color-grape-4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateX(0)';
                      e.currentTarget.style.borderColor = '';
                    }}
                    onClick={() => handlePersonaSelect(persona)}
                  >
                    <Group gap="md">
                      <Avatar radius="xl" size="lg" color="grape">
                        {persona.name.charAt(0)}
                      </Avatar>
                      <Box style={{ flex: 1 }}>
                        <Group gap="xs">
                          <Text fw={600}>{persona.name}</Text>
                          {persona.age && (
                            <Badge size="xs" variant="light">Age {persona.age}</Badge>
                          )}
                        </Group>
                        {persona.notes && (
                          <Text size="sm" c="dimmed" lineClamp={1}>
                            {persona.notes}
                          </Text>
                        )}
                      </Box>
                    </Group>
                  </Paper>
                ))
              )}
            </Stack>
          </ScrollArea>
        </Stack>
      </Modal>
    </Box>
  );
};

export default ProductsPage;
